import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import useEditLocation from "@/modules/lead-page/hooks/lead-info/edit-location.hook";
import useLeadInfo from "@/modules/lead-page/hooks/lead-info/lead-info.hook";
import LeadInfoField from "@/modules/lead-page/new/components/lead-info-field";
import FieldWrapper from "@/modules/lead-page/new/components/sidebar/fields/field-wrapper";
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { UUID } from "crypto";
import { SyntheticEvent, useContext, useEffect } from "react";
import { MapPinIcon } from "lucide-react";

const EditLocation = () => {
  const theme = useTheme();
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const isEditable = userHasPermission(
    { name: PermissionsEnum.JOURNEY_LOCATION, level: PermissionLevel.WRITE },
    currentUserPermissions
  );
  const { handleConfirmUpdate, isLoading, isSuccess } = useLeadInfo();
  const { location } = useEditLocation();
  useEffect(() => {
    if (isSuccess) {
      location.setEditLocationModalOpen(false);
      location.setSelectedLocation(null);
    }
  }, [isSuccess]);

  const handleLocationChange = (
    _event: SyntheticEvent<Element, Event>,
    value: {
      uid: UUID | string;
      label: string;
      id: number;
      locationType: string;
    } | null
  ) => {
    location.setSelectedLocation(value || null);
  };

  const mappedOptions =
    location.locationsData?.results?.map((loc) => ({
      label:
        loc?.area?.name ||
        loc?.compound?.name ||
        loc?.developer?.name ||
        loc.name,
      uid: loc.uid,
      id: loc?.area?.id || loc?.compound?.id || loc?.developer?.id || loc.uid,
      locationType: loc.locationType,
    })) || [];

  const renderContent = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <Autocomplete
          id="combo-box-demo"
          onInputChange={location.handleSearchChange}
          loading={location.locationsLoading}
          options={mappedOptions ?? []}
          sx={{
            width: "100%",
          }}
          value={location.selectedLocation as any}
          onChange={handleLocationChange}
          isOptionEqualToValue={(option, value) =>
            option.label === value?.label
          }
          renderInput={(params) => (
            <TextField {...params} label="Find Location" variant="outlined" />
          )}
        />
      </Box>
    );
  };

  const renderLocationWithIcon = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.nawy.spacing.xs,
        }}
      >
        <MapPinIcon size={16} color={theme.nawy.colors.semantic.text.primary} />
        <Typography
          sx={{
            color: theme.nawy.colors.semantic.text.primary,
            ...theme.nawy.typography.english.variants.body.md.regular,
          }}
        >
          {location.value}
        </Typography>
      </Box>
    );
  };

  const renderPlaceholder = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          alignContent: "center",
          justifyContent: "center",
          textAlign: "center",
          width: "100%",
          cursor: isEditable ? "pointer" : "default",
          gap: theme.nawy.spacing.xxs,
        }}
      >
        <MapPinIcon size={16} color={theme.nawy.colors.primitives.grey[700]} />
        <Typography
          sx={{
            color: theme.nawy.colors.primitives.grey[700],
            ...theme.nawy.typography.english.variants.body.md.regular,
          }}
        >
          Set Location
        </Typography>
      </Box>
    );
  };

  return (
    <LeadInfoField
      editable={isEditable}
      setEditModalOpen={location.setEditLocationModalOpen}
      editModalOpen={location.editLocationModalOpen}
      content={
        location.value !== "Not Set"
          ? renderLocationWithIcon()
          : renderPlaceholder()
      }
      sxInnerBox={{ justifyContent: "space-between" }}
      editModal={
        isEditable ? (
          <ResponsiveModal
            open={location.editLocationModalOpen}
            onClose={() => {
              location.setEditLocationModalOpen(false);
              location.setSelectedLocation(null);
            }}
            title="Edit Location"
            confirmButtonText="Save"
            cancelButtonText="Cancel"
            onConfirm={() => {
              handleConfirmUpdate({
                lead: {
                  locationUId: location.selectedLocation?.uid as UUID,
                },
              });
              location.setSelectedLocation(null);
            }}
            isLoading={isLoading}
            isConfirmDisabled={!location.selectedLocation}
            snapPoints={[1]}
          >
            <FieldWrapper>{renderContent()}</FieldWrapper>
          </ResponsiveModal>
        ) : (
          <></>
        )
      }
    />
  );
};

export default EditLocation;
